import { Loader, Modal, Tooltip, useMantineTheme } from '@mantine/core';
import {
  IconFile,
  IconFileTypeDocx,
  IconFileTypeHtml,
  IconFileTypePdf,
  IconFileTypePpt,
  IconFileTypeXls,
  IconPhoto,
} from '@tabler/icons-react';
import React, { useEffect, useState } from 'react';

import { Http } from '@mygpt/app/http';
import { downloadEmbeddingFile } from '@mygpt/app/chat/services';
import { useChatStore } from '@mygpt/app/chat/stores/useChatStore';
import { useFilePreviewStore } from '@mygpt/app/chat/stores/useFilePreviewStore';
import { pickReadableAccent } from '@mygpt/app/chat/utils/colorContrast';
import { openFilePreviewTab } from '@mygpt/app/chat/utils/filePreviewHandoff';
import { FileChipKind, parseFilePreviewLink, resolveFileChipKind } from '@mygpt/app/chat/utils/filePreviewLink';
import { Footnote } from '@mygpt/types';

import classes from './fileChip.module.css';

/**
 * 回答正文里的知识库文件卡片。
 *
 * 由 Markdown.tsx 的 a 渲染拦截 /preview/<file_id> 前缀链接后交给本组件（解析见 utils/filePreviewLink）。
 * 它和 <CITATION> 角标是两条并存的链路：角标是 RAG 命中片段，卡片是模型主动提到的文件。
 *
 * 点击后复用角标同一套预览面板（useFilePreviewStore → PDFViewer），小部件内（iframe 太窄）
 * 沿用 openFilePreviewTab 在新标签页打开 /preview/:fileId。
 */

const KIND_ICON: Record<FileChipKind, React.ComponentType<{ size?: number | string }>> = {
  pdf: IconFileTypePdf,
  word: IconFileTypeDocx,
  ppt: IconFileTypePpt,
  excel: IconFileTypeXls,
  image: IconPhoto,
  web: IconFileTypeHtml,
  file: IconFile,
};

interface FileChipProps {
  href: string;
  /** Markdown 链接的 title 位，后端约定放文件名 */
  title?: string;
  /** 链接文字 */
  children?: React.ReactNode;
  /** 本条回答的 reference_list，用于没有文件名时按 file_id 反查 */
  referenceList?: Footnote[];
  /** 链接前面的上下文片段，用于 PDF 高亮；不进 URL */
  snippet: string;
  /** 本条回答内第几个文件卡片，用于区分相同片段的高亮缓存 */
  order: number;
  /** 小部件自定义的品牌主色（widgetColor.chatColor） */
  accentColor?: string;
  /** 小部件自定义的正文文字色（widgetColor.botMessageTextColor） */
  textColor?: string;
  /** 小部件自定义的回答气泡色（widgetColor.botMessageColor），用来判断强调色在上面是否看得清 */
  bubbleColor?: string;
  /** 数字人场景：正文铺在视频上，白字 + 半透明白底 */
  isDigitalHuman?: boolean;
}

/**
 * 按场景算出卡片的配色变量；返回空对象时走 CSS 里的 Mantine 主题主色默认值。
 * color-mix 用于把任意强调色打淡做底色，Chrome 111+ / Safari 16.2+ 支持，
 * 不支持的旧浏览器会忽略该声明，退回透明底 + 强调色字，仍可读。
 */
const resolveChipStyle = (accent: string | undefined, isDigitalHuman?: boolean): React.CSSProperties => {
  if (isDigitalHuman) {
    return {
      '--file-chip-color': '#ffffff',
      '--file-chip-bg': 'rgba(255, 255, 255, 0.18)',
      '--file-chip-bg-hover': 'rgba(255, 255, 255, 0.3)',
    } as React.CSSProperties;
  }

  if (!accent) return {};

  return {
    '--file-chip-color': accent,
    '--file-chip-bg': `color-mix(in srgb, ${accent} 12%, transparent)`,
    '--file-chip-bg-hover': `color-mix(in srgb, ${accent} 20%, transparent)`,
  } as React.CSSProperties;
};

const toText = (node: React.ReactNode): string => {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(toText).join('');
  if (React.isValidElement(node)) return toText((node.props as { children?: React.ReactNode }).children);
  return '';
};

export const FileChip = ({
  href,
  title,
  children,
  referenceList,
  snippet,
  order,
  accentColor,
  textColor,
  bubbleColor,
  isDigitalHuman,
}: FileChipProps) => {
  const { pageSource } = useChatStore();
  const theme = useMantineTheme();
  const { setReferenceInfo, setExcelReferenceInfo } = useFilePreviewStore();
  const isWidget = pageSource === 'widget';

  // 主题主色的实际色值（与 --mantine-primary-color-light-color 同一档），用于和自定义气泡色比对比度
  const primaryShade = typeof theme.primaryShade === 'number' ? theme.primaryShade : theme.primaryShade.light;
  const themeColor = theme.colors[theme.primaryColor]?.[primaryShade];
  const chipStyle = resolveChipStyle(
    pickReadableAccent({ accentColor, textColor, bubbleColor, themeColor }),
    isDigitalHuman
  );

  const [imageUrl, setImageUrl] = useState<string>();
  const [imageLoading, setImageLoading] = useState(false);

  // 图片弹窗关闭后释放 blob URL
  useEffect(() => {
    return () => {
      if (imageUrl) window.URL.revokeObjectURL(imageUrl);
    };
  }, [imageUrl]);

  const link = parseFilePreviewLink(href);
  const referenceName = link ? referenceList?.find((item) => item.file_id === link.fileId)?.title : undefined;
  const linkText = toText(children);

  // 文件名优先级：title 位 → references 文件名 → 链接文字（仅当它看起来像文件名）
  const fileName = title?.trim() || referenceName?.trim() || (resolveFileChipKind(linkText) ? linkText : '');

  // 类型判定顺序（与后端约定一致）：title 后缀 → ?type= → references 文件名 → 链接文字后缀 → 通用
  const kind: FileChipKind =
    resolveFileChipKind(title) ||
    resolveFileChipKind(link?.type ? `x.${link.type}` : '') ||
    resolveFileChipKind(referenceName) ||
    resolveFileChipKind(linkText) ||
    'file';

  const Icon = KIND_ICON[kind];

  // 接口失败的用户提示由 http 层拦截器统一弹 toast，这里只留日志，避免重复提示
  const reportError = (scene: string) => (error: unknown) => {
    console.error(`文件卡片：${scene}失败`, error);
  };

  const download = (fileId: string) => {
    downloadEmbeddingFile({ file_id: fileId, filename: fileName || fileId }).catch(reportError('下载'));
  };

  const openImage = async (fileId: string) => {
    // <img src> 直连下载接口不带 Authorization，私有 bot 会 401，统一走 axios blob
    setImageLoading(true);
    try {
      const response = await Http.use().get<Blob>(`/embeddings/files/${fileId}/download`, { responseType: 'blob' });
      setImageUrl(window.URL.createObjectURL(response.data));
    } catch (error) {
      reportError('图片加载')(error);
    } finally {
      setImageLoading(false);
    }
  };

  const openHtml = (fileId: string) => {
    // 必须在点击的同步调用栈里先开窗，否则等 blob 回来再 open 会被浏览器拦截
    const target = window.open('', '_blank');
    if (!target) return;
    Http.use()
      .get<Blob>(`/embeddings/files/${fileId}/download`, { responseType: 'blob' })
      .then((response) => {
        target.location.href = window.URL.createObjectURL(response.data);
      })
      .catch((error) => {
        target.close();
        reportError('网页加载')(error);
      });
  };

  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    if (!link) return;
    const { fileId, page, sheet, rows } = link;

    switch (kind) {
      case 'pdf':
      case 'word':
      case 'ppt': {
        // docx/pptx 由 PDFViewer 固定走 to_pdf=true 转 PDF 后展示
        if (isWidget) {
          openFilePreviewTab({
            kind: 'pdf',
            file_id: fileId,
            page,
            content: snippet,
            citationOrder: order,
            title: fileName,
          });
          return;
        }
        setReferenceInfo({ file_id: fileId, page, content: snippet, citationOrder: order, title: fileName });
        return;
      }
      case 'excel': {
        // 表格预览只能画被引用的行片段；没给 rows 就没有东西可画，退回下载原文件
        if (!rows.length) {
          download(fileId);
          return;
        }
        if (isWidget) {
          openFilePreviewTab({ kind: 'excel', file_id: fileId, rows, sheet, title: fileName });
          return;
        }
        setExcelReferenceInfo({ file_id: fileId, rows, sheet, title: fileName });
        return;
      }
      case 'image':
        openImage(fileId);
        return;
      case 'web':
        openHtml(fileId);
        return;
      default:
        download(fileId);
    }
  };

  return (
    <>
      <Tooltip label={fileName} withArrow disabled={!fileName}>
        <a href={href} className={classes.fileChip} style={chipStyle} onClick={handleClick}>
          {imageLoading ? <Loader size={12} /> : <Icon size={14} />}
          <span className={classes.fileChipText}>{linkText || fileName || href}</span>
        </a>
      </Tooltip>
      {kind === 'image' && (
        <Modal
          opened={!!imageUrl}
          onClose={() => setImageUrl(undefined)}
          title={fileName}
          size="auto"
          centered
          zIndex={500}
          padding={0}
          classNames={{ header: 'p-4 m-0 bg-black/70 text-white', close: 'text-white hover:bg-black/50' }}
        >
          <div className={classes.imageModalBody}>{imageUrl && <img src={imageUrl} alt={fileName} />}</div>
        </Modal>
      )}
    </>
  );
};
